import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import { Box, Divider, IconButton, Stack, styled, SvgIcon, Tooltip, Typography, useTheme } from '@mui/material';
import { first, isBoolean, isEmpty, isUndefined, truncate } from 'lodash';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Briefcase, Columns, Edit, Printer, UserCheck } from 'react-feather';

// Components
import ButtonBookmarkWhite from 'src/components/common/ButtonBookmarkWhite';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FlagCountry from 'src/components/common/FlagCountry';
import IconFeather from 'src/components/common/Icon';
import LoadingSkeleton from 'src/components/common/LoadingSkeleton';
import LogoCompany from 'src/components/common/LogoCompany';
import TranslateText from 'src/components/common/TranslateText';

// Utilities
import { CreditCoinIcon } from 'src/components/Icons';
import FormBookmarkNote from 'src/features/Bookmark/FormBookmarkNote';
import {
  CompanyProfileTagsMapping,
  ICompanyProfileTagProps,
} from 'src/features/CompanyDetail/Common/CompanyProfileTags';
import { TypeContributeCompany } from 'src/features/CompanyDetail/Common/config';
import { useFeatureAccess } from 'src/hooks/useFeatureAccess';
import useRefetch from 'src/hooks/useRefetch';
import { BookmarkTypes } from 'src/model/bookmark';
import { useMutateExportCompanyCertification, useMutatePrintCompany } from 'src/services/mutate';
import { useMutateCreateBookmark, useMutateUpdateBookmarkByDataUuid } from 'src/services/mutate/useMutateBookmark';
import { QUERY_KEY_USAGE, useQueryUserInfo } from 'src/services/queries';
import { useQueryGetBookmarkByDataUuid } from 'src/services/queries/useQueryBookmark';
import { EmptyText, MAX_LENGTH_CONTENT, UnknownText } from 'src/utils/constants/config';
import { TypeCompanyProfileType } from 'src/utils/constants/enum';
import { FeatureAction } from 'src/utils/constants/featuresPoint';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getCountryByCountryCode } from 'src/utils/helpers/stringHelpers';
import { useCompanyContribute } from 'src/zustand/useCompanyContribute';
import { FormType, useFormSearch } from 'src/zustand/useFormSearch';
import { NotifyType, useNotifyCreditCosts } from 'src/zustand/useNotifyCreditCosts';
import useSnackbarStore from 'src/zustand/useSnackbarStore';
// import { usePrinter } from 'src/hooks/usePrinter';

const TypographyWhite = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  overflow: 'hidden',
}));

const StyledButtonWithLoading = styled(ButtonWithLoading)(() => ({
  width: 'fit-content',
}));

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  background: 'linear-gradient(180deg, #7551FF 0%, #3311DB 100%)',
  boxShadow: '0px 0px 50px 0px rgba(112, 114, 176, 0.08)',
}));

const StyledIconWhite = styled(SvgIcon)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: 16,
}));

const IconButtonWhite = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: 16,
  border: `1px solid ${theme.palette.common.white}`,
}));

const StyledButtonShowMore = styled(Typography)(() => ({
  textDecoration: 'underline',
  cursor: 'pointer',
}));

const CompanyInformation = ({
  company,
  isFetching,
  isHideActions = false,
  companyFallback,
}: {
  company: ICompanyDetail;
  isFetching: boolean;
  isHideActions?: boolean;
  companyFallback?: TradeDataCompanyDetail;
}) => {
  const theme = useTheme();
  const { search, pathname } = location;
  const { refetchQuery } = useRefetch();
  const [visibleMore, setVisibleMore] = useState(false as boolean);
  const [loadDataFirstTime, setLoadDataFirstTime] = useState(false);
  const [isOpenBookmarkNoteForm, setIsOpenBookmarkNoteForm] = useState(false);
  const [note, setNote] = useState<string>('');
  const [isBookmark, setIsBookmark] = useState(false);

  // Custom hooks
  const { handleOpenNotify } = useNotifyCreditCosts((state) => state);
  const { dispatch: dispatchSnackbar } = useSnackbarStore();
  const { handleOpenSearch } = useFormSearch((state) => state);
  const { handleOpenContribute } = useCompanyContribute((state) => state);
  const { getFeaturePoints } = useFeatureAccess();
  const pointsPrintCompany = getFeaturePoints(URL_ENDPOINT.myReport.printCompanyDetail, FeatureAction.VIEW);
  // const pointsPrintCompany = useGetPointsFeature(
  //   LIST_FEATURES.INTERNAL_REPORT_COMPANY_DUE_DILIGENCE,
  //   LIST_ACTIONS.PRINT,
  // );

  // react-query hooks
  const { data: dataBookmark } = useQueryGetBookmarkByDataUuid(BookmarkTypes.Company, company?.id || '');
  const { mutateAsync, isPending } = useMutateExportCompanyCertification();
  const { mutateAsync: createBookmarkMutate, isPending: isPendingCreateBookmark } = useMutateCreateBookmark();
  const { mutateAsync: updateBookmarkMutate, isPending: isPendingUpdateBookmark } = useMutateUpdateBookmarkByDataUuid();
  const printCompanyMutation = useMutatePrintCompany();
  const { data: currentUser } = useQueryUserInfo();

  const isNonOfficial = (company?.official_tags || []).includes(TypeCompanyProfileType.NON_OFFICIAL);
  const companyDescription = company?.description || companyFallback?.description || '';
  const companyName = company?.name || companyFallback?.name || '';

  const companyCountry = useMemo(() => {
    return getCountryByCountryCode(company?.country || companyFallback?.country_code || '');
  }, [company?.country, companyFallback?.country_code]);

  const companyProfileTags: ((props: ICompanyProfileTagProps) => JSX.Element)[] = useMemo(() => {
    if (!company?.id && companyFallback?.id) {
      return [CompanyProfileTagsMapping[TypeCompanyProfileType.NON_OFFICIAL]];
    }
    return (
      company?.official_tags?.map((tag: TypeCompanyProfileType) => {
        return CompanyProfileTagsMapping[tag];
      }) || []
    );
  }, [company?.official_tags]);

  const handleOpenBookmarkNoteForm = () => {
    setIsOpenBookmarkNoteForm(true);
  };

  const handleCloseBookmarkNoteForm = () => {
    setIsOpenBookmarkNoteForm(false);
  };

  const handleSubmitBookmarkNoteForm = useCallback(async () => {
    const response = isBookmark
      ? await updateBookmarkMutate({
          type: BookmarkTypes.Company,
          dataUuid: company?.id || '',
          body: {
            note,
          },
        })
      : await createBookmarkMutate({
          type: BookmarkTypes.Company,
          body: {
            data_uuid: company?.id || '',
            note,
          },
        });

    if (response?.status_code === SUCCESS_CODE.CREATE) {
      setIsBookmark(true);
    }

    handleCloseBookmarkNoteForm();
  }, [company?.id, note, isBookmark]);

  useEffect(() => {
    // After fetch data done first time
    if (isFetching === false && loadDataFirstTime === false) {
      setLoadDataFirstTime(true);
    }
  }, [isFetching]);

  useEffect(() => {
    // Fetch data bookmark done
    setIsBookmark(!isEmpty(dataBookmark?.data)); // If data bookmark is empty => This company is not marked yet.
    if (isEmpty(dataBookmark?.data)) {
      setNote('');
    } else {
      setNote(dataBookmark?.data?.note || '');
    }
  }, [dataBookmark?.data]);

  const handleExport = async () => {
    if (isUndefined(pointsPrintCompany?.planName)) {
      handleOpenNotify({
        type: NotifyType.NotEnoughCredit,
        showCloseIcon: true,
      });
      return;
    }

    const response = await printCompanyMutation.mutateAsync({
      pathname: pathname + '/print' + search,
      user_uuid: currentUser.data?.uuid ?? '',
    });

    if (!(response instanceof Blob)) {
      dispatchSnackbar({
        open: true,
        title: 'Error',
        description: 'Export fail. Please try again.',
        variant: 'standard',
        color: 'error',
      });
      return;
    }

    // Tạo URL từ `Blob`
    const url = URL.createObjectURL(response);

    // Tạo một thẻ `<a>` tạm thời để kích hoạt tải file
    const a = document.createElement('a');
    a.href = url;
    a.download = 'company_certification.pdf'; // Tên file PDF được tải về

    // Thêm thẻ `a` vào DOM và kích hoạt sự kiện click
    document.body.appendChild(a);
    a.click();

    // Sau khi tải xong, xóa thẻ `a` và URL object
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    refetchQuery([QUERY_KEY_USAGE]);
  };

  const renderDescription = () => {
    if (companyDescription.length <= MAX_LENGTH_CONTENT.COMPANY_INFORMATION) {
      return (
        <TranslateText
          buttonProps={{
            color: 'inheritWhite',
          }}
          disableTranslation={isHideActions}
          text={companyDescription}
          render={(text: string) => (
            <TypographyWhite align="left" variant="body2">
              {text}
            </TypographyWhite>
          )}
        />
      );
    }

    if (!visibleMore) {
      return (
        <TypographyWhite align="left" variant="body2">
          {truncate(companyDescription, { length: MAX_LENGTH_CONTENT.COMPANY_INFORMATION })}{' '}
          <StyledButtonShowMore
            display={'inline'}
            onClick={() => setVisibleMore(!visibleMore)}
            variant="subtitle2"
            color={'inherit'}
          >
            Show More
          </StyledButtonShowMore>
        </TypographyWhite>
      );
    } else {
      return (
        <TypographyWhite align="left" variant="body2">
          {companyDescription}{' '}
          <StyledButtonShowMore
            display={'inline'}
            onClick={() => setVisibleMore(!visibleMore)}
            variant="subtitle2"
            color={'inherit'}
          >
            Show Less
          </StyledButtonShowMore>
        </TypographyWhite>
      );
    }
  };

  if (isFetching && !loadDataFirstTime) {
    return (
      <StyledBox>
        <LoadingSkeleton count={5} height={30} />
      </StyledBox>
    );
  }

  const renderButtonActions = () => {
    if (isBoolean(isHideActions) && isHideActions) return <Fragment></Fragment>;
    return (
      <Stack mt={4} direction={'row'} spacing={2}>
        <StyledButtonWithLoading
          variant="outlined"
          color="inheritWhite"
          isRounded
          endIcon={<IconFeather icon={<Columns />} muiProps={{ width: '16' }} />}
          size="medium"
          onClick={() => {
            handleOpenSearch(FormType.CompareCompany, company);
          }}
        >
          Compare Companies
        </StyledButtonWithLoading>
        <Tooltip title="Claim Company">
          <StyledButtonWithLoading
            variant="outlined"
            color="inheritWhite"
            isRounded
            endIcon={<IconFeather icon={<Edit />} muiProps={{ width: '16' }} />}
            size="medium"
            onClick={() => handleOpenContribute(TypeContributeCompany.ClaimCompanyProfile)}
          >
            Contribute DATA
          </StyledButtonWithLoading>
        </Tooltip>
        <Tooltip title="Print">
          <StyledButtonWithLoading
            variant="outlined"
            color="inheritWhite"
            isRounded
            startIcon={<IconFeather icon={<Printer />} muiProps={{ fontSize: 'small' }} />}
            endIcon={
              pointsPrintCompany?.planName ? (
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={0.5}
                  py={0.5}
                  px={1}
                  borderRadius={theme.shape.borderRadius}
                  bgcolor="rgba(255, 255, 255, 0.12)"
                >
                  <Typography variant="caption2" component="span" color="white">
                    {pointsPrintCompany?.creditCosts}
                  </Typography>
                  <CreditCoinIcon fontSize="small" />
                </Stack>
              ) : (
                <CreditCoinIcon fontSize="small" />
              )
            }
            isLoading={printCompanyMutation.isPending}
            size="medium"
            onClick={() => handleExport()}
            sx={{ pointerEvents: printCompanyMutation.isPending ? 'none' : '' }}
          >
            PRINT
          </StyledButtonWithLoading>
        </Tooltip>
        <Tooltip title="Claim Company">
          <IconButtonWhite onClick={() => handleOpenContribute(TypeContributeCompany.ClaimCompanyProfile)}>
            <IconFeather icon={<UserCheck />} muiProps={{ fontSize: 'small' }} />
          </IconButtonWhite>
        </Tooltip>
        <ButtonBookmarkWhite
          isMarked={isBookmark}
          buttonProps={{
            onClick: () => handleOpenBookmarkNoteForm(),
          }}
        />
      </Stack>
    );
  };

  return (
    <StyledBox>
      <Stack direction="row" alignItems="center" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
        <Box flex={3}>
          <Box alignItems="center" mb={1} display="flex">
            <LogoCompany
              domain={first(company.websites)}
              sx={{
                backgroundColor: theme.palette.background.default,
                borderRadius: theme.shape.borderRadius / 4,
                p: 0.5,
                mr: 1,
              }}
              logoURL={company.logo_url}
              width={48}
              height={48}
            />

            <Stack flexDirection={'row'} gap={1}>
              <TypographyWhite align="left" variant="h5" gutterBottom>
                {companyName.toUpperCase() ?? EmptyText}
              </TypographyWhite>
              {companyProfileTags.map((CompanyProfileTag, index) => {
                return <CompanyProfileTag colorType={'solid'} key={index} />;
              })}
            </Stack>
          </Box>
          {renderDescription()}
          {renderButtonActions()}
        </Box>
        <Box display="flex" flexDirection="column" flex={1} minWidth={245} gap={2}>
          {company?.registered_address && (
            <Stack spacing={0.5} direction="row" alignItems="center">
              <StyledIconWhite>
                <RoomOutlinedIcon />
              </StyledIconWhite>
              <TypographyWhite variant="caption" align="left">
                {(company?.registered_address || '').toUpperCase() || EmptyText}
              </TypographyWhite>
            </Stack>
          )}
          {company?.company_code && (
            <Stack spacing={0.5} direction="row" alignItems="center">
              <IconFeather
                icon={<Briefcase />}
                muiProps={{ sx: { color: theme.palette.common.white, fontSize: 16 } }}
              />
              <TypographyWhite variant="caption" align="left">
                {company?.company_code ?? EmptyText}
              </TypographyWhite>
              <Tooltip arrow title="Company number">
                <StyledIconWhite>
                  <InfoOutlinedIcon />
                </StyledIconWhite>
              </Tooltip>
            </Stack>
          )}
          {companyCountry?.code && (
            <Stack spacing={0.5} direction="row" alignItems="center">
              <FlagCountry code={companyCountry?.code} width={16} colorDefaultFlag={theme.palette.common.white} />
              <TypographyWhite variant="caption" align="left">
                {companyCountry?.label || EmptyText}
              </TypographyWhite>
              <Tooltip arrow title="Country">
                <StyledIconWhite>
                  <InfoOutlinedIcon />
                </StyledIconWhite>
              </Tooltip>
            </Stack>
          )}
          <Stack spacing={0.5} direction="row" alignItems="center">
            <StyledIconWhite>
              <TaskAltOutlinedIcon />
            </StyledIconWhite>
            <TypographyWhite variant="caption" align="left">
              {/* {company?.status ?? EmptyText} */}
              {company?.status && !isNonOfficial ? company?.status : UnknownText}
            </TypographyWhite>
            <Tooltip arrow title="Status">
              <StyledIconWhite>
                <InfoOutlinedIcon />
              </StyledIconWhite>
            </Tooltip>
          </Stack>
          {/* <Stack spacing={0.5} direction="row" alignItems="center">
            <IconFeather icon={<UserCheck />} muiProps={{ sx: { color: theme.palette.common.white, fontSize: 16 } }} />
            <TypographyWhite variant="caption" align="left">
            {company?.is_sanctioned ? 'Claimed' : 'Unclaimed'}
            </TypographyWhite>
            </Stack> */}
        </Box>
      </Stack>
      <FormBookmarkNote
        open={isOpenBookmarkNoteForm}
        handleClose={handleCloseBookmarkNoteForm}
        handleSubmit={() => handleSubmitBookmarkNoteForm()}
        value={note}
        onChange={(value) => setNote(value)}
        isLoading={isPendingCreateBookmark || isPendingUpdateBookmark}
      />
    </StyledBox>
  );
};

export default CompanyInformation;
